import React from "react";
import { CustomerInfoWrapper, SeparateLine, TitleWrapper } from "./Styled";
import { Avatar } from "@mui/material";
import { color } from "../../../../shared/styles";
import { Customer, CustomerContact } from "../../../../data/entities";
import Section, { Info } from "./Section";

interface CustomerInfoProps {
    customer?: Customer | null;
}

interface InfoDef {
    title: string;
    field: string;
    toValue?: (value: any) => string | undefined;
}

const contactFieldToValue = (field: keyof CustomerContact) => (value: any) => {
    // Ensure value is an array before calling find
    if (!Array.isArray(value)) {
        return undefined;
    }
    const defaultContact = value?.find((c: any) => c.contact.is_default)?.contact;
    return defaultContact?.[field];
};

const SECTION_INFOs = [
    {
        title: 'Thông tin khách hàng',
        infoDefinitions: [
            {
                title: 'Số điện thoại',
                field: 'phone',
            },
            {
                title: 'Email',
                field: 'email',
            },
            {
                title: 'Fax',
                field: 'fax',
            },
            {
                title: 'Địa chỉ',
                field: 'address',
                toValue: (value: string) => {
                    const addressParts = value.split(', ');
                    const addressObj: Record<string, string> = {};
                    addressParts.forEach((part) => {
                        const [key, value] = (part.replaceAll(/['{}]/g, "")).split(': ');
                        addressObj[key] = value;
                    });
                    return [addressObj.home_address, addressObj.ward, addressObj.district, addressObj.city, addressObj.country].filter(Boolean).join(', ');
                },
            },
        ],
    },
    {
        title: "Thông tin người liên hệ",
        infoDefinitions: [
            {
                title: "Người liên hệ",
                field: "name",
                toValue: contactFieldToValue("name"),
            },
            {
                title: "Email",
                field: "email",
                toValue: contactFieldToValue("email"),
            },
            {
                title: "Số điện thoại",
                field: "phone",
                toValue: contactFieldToValue("phone"),
            },
            {
                title: "Cấp bậc",
                field: "position",
                toValue: contactFieldToValue("position"),
            },
        ],
    },
];

const infoListFactory = (customer: any, infoList: InfoDef[]) => {
    if (!customer) return infoList.map(infoDef => ({ title: infoDef.title, value: undefined }));
    
    return infoList.map((infoDef: InfoDef) => {
        let value = customer[infoDef.field] 
            ? (infoDef.toValue ? infoDef.toValue(customer[infoDef.field]) : customer[infoDef.field])
            : undefined;

        return {
            title: infoDef.title,
            value,
        };
    });
};

const CustomerInfo = ({ customer }: CustomerInfoProps) => {
    // Handle null/undefined customer
    if (!customer) {
        return (
            <CustomerInfoWrapper>
                <TitleWrapper>
                    <Avatar
                        sx={{
                            width: "64px",
                            height: "64px",
                            color: "black",
                            backgroundColor: color.status.WARNING_LIGHT,
                            fontFamily: "Noto Sans",
                        }}
                    >
                        N/A
                    </Avatar>
                    <span style={{ color: color.status.WARNING }}>Không có thông tin khách hàng</span>
                </TitleWrapper>
                <SeparateLine />
                <div style={{ padding: "16px", color: color.secondary.DARK_GREY }}>
                    Đơn hàng này không có thông tin khách hàng. Hãy liên hệ quản trị viên để được hỗ trợ.
                </div>
            </CustomerInfoWrapper>
        );
    }

    return (
        <CustomerInfoWrapper>
            <TitleWrapper>
                <Avatar
                    sx={{
                        width: "64px",
                        height: "64px",
                        color: "black",
                        backgroundColor: color.status.INFO_LIGHT,
                        fontFamily: "Noto Sans",
                    }}
                >
                    CK
                </Avatar>
                {customer.name}
            </TitleWrapper>
            <SeparateLine />
            {SECTION_INFOs.map((sectionInfo: any, index: number) => (
                <Section
                    key={`section-${index}`}
                    title={sectionInfo.title}
                    infoList={infoListFactory(
                        customer,
                        sectionInfo.infoDefinitions
                    )}
                />
            ))}
        </CustomerInfoWrapper>
    );
};

export default CustomerInfo;
